import { browserAgent } from "@/shared/telemetry/newrelic";
import type { LogEntry } from "./log";
import { generateRFC3339Timestamp } from "./log";

export interface LoggingConfig {
  enabled: boolean;
  dashcamMode: {
    enabled: boolean;
    bufferMaxDuration: number;
    bufferMaxSize: number;
  };
  matchers: {
    level?: string;
    package?: string;
    context?: Map<string, string>;
  }[];
}

export interface Logger {
  setContext: (key: string, value: unknown) => void;
  debug: (pkg: string, message: string, payload?: unknown) => void;
  info: (pkg: string, message: string, payload?: unknown) => void;
  warn: (pkg: string, message: string, payload?: unknown) => void;
  error: (
    pkg: string,
    message: string,
    error?: Error,
    payload?: unknown,
  ) => void;
}

export type LogContext = Record<string, unknown>;

type WorkerMessage =
  | { type: "INIT"; config: LoggingConfig; token: string }
  | { type: "BATCH"; entries: LogEntry<unknown>[] }
  | { type: "FLUSH" };

/**
 * Sanitize data to remove non-serializable values (functions, circular refs, etc.)
 * This ensures data can be safely sent to web workers via postMessage
 */
function sanitizeForWorker(value: unknown, seen = new WeakSet()): unknown {
  // Handle null and undefined
  if (value === null || value === undefined) {
    return value;
  }

  // Handle primitives
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return value;
  }

  // Handle functions - replace with string representation
  if (typeof value === "function") {
    return `[Function: ${value.name || "anonymous"}]`;
  }

  // Handle symbols - replace with string representation
  if (typeof value === "symbol") {
    return `[Symbol: ${value.toString()}]`;
  }

  // Handle Date objects
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Handle Error objects - extract serializable properties
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForWorker(item, seen));
  }

  // Handle objects (but check for circular references)
  if (typeof value === "object") {
    // Check for circular reference
    if (seen.has(value as object)) {
      return "[Circular Reference]";
    }

    seen.add(value as object);

    try {
      const sanitized: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(
        value as Record<string, unknown>,
      )) {
        try {
          sanitized[key] = sanitizeForWorker(val, seen);
        } catch (err) {
          // If sanitization fails for a property, skip it
          sanitized[key] =
            `[Error sanitizing: ${err instanceof Error ? err.message : String(err)}]`;
        }
      }
      return sanitized;
    } catch (err) {
      return `[Error sanitizing object: ${err instanceof Error ? err.message : String(err)}]`;
    }
  }

  // Fallback for any other types
  try {
    return String(value);
  } catch {
    return "[Non-serializable value]";
  }
}

/**
 * Sanitize a log entry to ensure it can be safely sent to a worker
 */
function sanitizeLogEntry(entry: LogEntry<unknown>): LogEntry<unknown> {
  return {
    ...entry,
    payload:
      entry.payload !== undefined
        ? sanitizeForWorker(entry.payload)
        : undefined,
    context: entry.context
      ? (sanitizeForWorker(entry.context) as Record<string, unknown>)
      : undefined,
  };
}

// Global logger instance (created immediately, initialized later)
let globalLoggerInstance: LoggerImpl | null = null;

// Create logger instance immediately and expose globally
declare global {
  interface Window {
    logger?: Logger;
  }
}

export class LoggerImpl implements Logger {
  private context: LogContext = {};
  private sessionId: string = "";
  private config: LoggingConfig | null = null;
  private worker: Worker | null = null;
  private batchBuffer: LogEntry<unknown>[] = [];
  private batchSize = 10; // Process every 10 logs
  private batchTimer: number | null = null;
  private unloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;
  private readonly MAX_BUFFER_SIZE = 1000;
  public isInitialized = false;
  private logIndex = 0; // Sequential counter for log entries

  constructor() {
    // Instance is created immediately, will be initialized later
  }

  /**
   * Initialize the logger with config and context
   */
  initialize(
    context: LogContext,
    config: LoggingConfig,
    token: string,
    sessionId: string,
  ): void {
    if (this.isInitialized) {
      console.warn(
        "[Logger] Logger already initialized. Skipping re-initialization.",
      );
      return;
    }

    this.context = context;
    this.sessionId = sessionId;
    this.config = config;

    // Initialize worker
    this.worker = new Worker(
      new URL("./background-worker.ts", import.meta.url),
      { type: "module" },
    );

    this.worker.onmessage = (e: MessageEvent) => {
      const data = e.data as { type?: string; error?: string; endpoint?: string; logCount?: number };
      if (data?.type === "LOGGING_API_FAILED") {
        try {
          browserAgent.log("LOGGING_API_FAILED", {
            customAttributes: {
              error: data.error,
              endpoint: data.endpoint,
              logCount: data.logCount,
            },
          });
        } catch (error) {
          console.error("[Logger] Failed to send logging API failed event to New Relic:", error);
        }
      }
    };

    // Send config and token to worker
    try {
      this.worker.postMessage({
        type: "INIT",
        config: this.config,
        token,
      } as WorkerMessage);
    } catch (error) {
      // Log error but don't throw - logging should never break the app
      console.error("[Logger] Failed to initialize worker:", error);
    }

    // Start periodic processing
    this.startPeriodicProcessing();

    // Register unload listener
    this.registerUnloadListener();

    // Mark as initialized and process buffered logs
    this.isInitialized = true;
    this.processPreInitBuffer();
  }

  private startPeriodicProcessing(): void {
    // Process every 5 seconds
    this.batchTimer = window.setInterval(() => {
      this.flushBatch();
    }, 5000);
  }

  private registerUnloadListener(): void {
    const worker = this.worker;
    this.unloadHandler = () => {
      try {
        // Flush any remaining logs in batchBuffer to worker first
        this.flushBatch();
        // Then send final flush message to worker to process dashcam and send queue
        if (worker) {
          worker.postMessage({ type: "FLUSH" } as WorkerMessage);
        }
      } catch {
        // Silently fail - we're in unload handler, can't do much
        // Logging should never break the app
      }
    };

    window.addEventListener("beforeunload", this.unloadHandler);
  }

  private addToBatch(entry: LogEntry<unknown>, isError: boolean = false): void {
    try {
      // Check buffer size limit
      if (this.batchBuffer.length >= this.MAX_BUFFER_SIZE) {
        // Buffer is full, output warning to console (only once)
        if (this.batchBuffer.length === this.MAX_BUFFER_SIZE) {
          console.warn("[Logger] Buffer is full. Some logs may be lost.");
        }
        // Don't add more logs to prevent unbounded growth
        return;
      }

      // Increment log index and attach session_id, index, and context to log entry before batching
      this.logIndex++;
      const entryWithContext: LogEntry<unknown> = {
        ...entry,
        session_id: this.sessionId,
        index: this.logIndex,
        ...(Object.keys(this.context).length > 0 && {
          context: { ...this.context },
        }),
      };
      this.batchBuffer.push(entryWithContext);

      // Only flush if worker is ready
      if (!this.worker || !this.config) {
        // Worker not ready yet, just buffer
        return;
      }

      // Immediate processing on error
      if (isError) {
        this.flushBatch();
        // Error detection and dashcam flush is handled in worker's BATCH handler
      }
      // Batch processing when threshold reached
      else if (this.batchBuffer.length >= this.batchSize) {
        this.flushBatch();
      }
    } catch (error) {
      // Logging should never break the app
      console.error("[Logger] Failed to add log to batch:", error);
    }
  }

  private outputToConsole(
    level: "debug" | "info" | "warn" | "error",
    pkg: string,
    message: string,
    error?: Error,
    payload?: unknown,
  ): void {
    const prefix = `[${pkg}]`;
    const consoleArgs: unknown[] = [prefix, message];

    if (payload !== undefined) {
      consoleArgs.push(payload);
    }

    if (error) {
      consoleArgs.push(error);
    }

    // Output to appropriate console method
    switch (level) {
      case "debug":
        console.debug(...consoleArgs);
        break;
      case "info":
        console.log(...consoleArgs);
        break;
      case "warn":
        console.warn(...consoleArgs);
        break;
      case "error":
        console.error(...consoleArgs);
        break;
    }
  }

  private processPreInitBuffer(): void {
    if (this.batchBuffer.length === 0) return;

    console.log(
      `[Logger] Processing ${this.batchBuffer.length} buffered logs from before initialization`,
    );

    // Send buffered logs to worker
    // Note: Context is already attached to entries in addToBatch, but we update
    // with current context in case it changed during initialization
    if (this.config && this.config.enabled && this.worker) {
      // Update session_id, index, and context on all buffered entries with current values
      // (entries buffered before init won't have session_id/index/context, entries after init will)
      const entriesWithContext = this.batchBuffer.map((entry) => {
        // Increment index for each buffered entry
        this.logIndex++;
        return {
          ...entry,
          session_id: this.sessionId,
          index: this.logIndex,
          ...(Object.keys(this.context).length > 0 && {
            context: { ...this.context },
          }),
        };
      });

      // Send to worker (error detection is handled in worker's BATCH handler)
      try {
        const sanitizedEntries = entriesWithContext.map((entry) =>
          sanitizeLogEntry(entry),
        );
        this.worker.postMessage({
          type: "BATCH",
          entries: sanitizedEntries,
        } as WorkerMessage);
      } catch (error) {
        // Log error but don't throw - logging should never break the app
        console.error(
          "[Logger] Failed to send buffered logs to worker:",
          error,
        );
      }

      // Clear the processed entries from buffer
      this.batchBuffer = [];
    }
  }

  private flushBatch(): void {
    if (this.batchBuffer.length === 0 || !this.worker) return;

    try {
      // Ensure all entries have session_id set (in case logs were created before initialization)
      const entriesWithSessionId = this.batchBuffer.map((entry) => ({
        ...entry,
        session_id: entry.session_id || this.sessionId || "",
        // Also ensure index is set if missing
        index: entry.index || ++this.logIndex,
      }));

      // Sanitize entries before sending to worker
      const sanitizedEntries = entriesWithSessionId.map((entry) =>
        sanitizeLogEntry(entry),
      );

      // Send batch to worker
      this.worker.postMessage({
        type: "BATCH",
        entries: sanitizedEntries,
      } as WorkerMessage);

      this.batchBuffer = [];
    } catch (error) {
      // Log error but don't throw - logging should never break the app
      console.error("[Logger] Failed to flush batch to worker:", error);
      // Clear buffer anyway to prevent memory leaks
      this.batchBuffer = [];
    }
  }

  debug(pkg: string, message: string, payload?: unknown) {
    try {
      // Always output to console
      this.outputToConsole("debug", pkg, message, undefined, payload);

      // Add to batch (will buffer if worker not ready, or send if ready and enabled)
      if (this.config?.enabled !== false) {
        this.addToBatch({
          timestamp: generateRFC3339Timestamp(),
          package: pkg,
          level: "debug",
          message,
          payload,
        });
      }
    } catch {
      // Logging should never break the app - fallback to console only
      console.debug(`[${pkg}]`, message, payload);
    }
  }

  info(pkg: string, message: string, payload?: unknown) {
    try {
      // Always output to console
      this.outputToConsole("info", pkg, message, undefined, payload);

      // Add to batch (will buffer if worker not ready, or send if ready and enabled)
      if (this.config?.enabled !== false) {
        this.addToBatch({
          timestamp: generateRFC3339Timestamp(),
          package: pkg,
          level: "info",
          message,
          payload,
        });
      }
    } catch {
      // Logging should never break the app - fallback to console only
      console.log(`[${pkg}]`, message, payload);
    }
  }

  warn(pkg: string, message: string, payload?: unknown) {
    try {
      // Always output to console
      this.outputToConsole("warn", pkg, message, undefined, payload);

      // Add to batch (will buffer if worker not ready, or send if ready and enabled)
      if (this.config?.enabled !== false) {
        this.addToBatch({
          timestamp: generateRFC3339Timestamp(),
          package: pkg,
          level: "warn",
          message,
          payload,
        });
      }
    } catch {
      // Logging should never break the app - fallback to console only
      console.warn(`[${pkg}]`, message, payload);
    }
  }

  error(pkg: string, message: string, error?: Error, payload?: unknown) {
    try {
      // Always output to console
      this.outputToConsole("error", pkg, message, error, payload);

      // Add to batch (will buffer if worker not ready, or send if ready and enabled)
      if (this.config?.enabled !== false) {
        this.addToBatch(
          {
            timestamp: generateRFC3339Timestamp(),
            package: pkg,
            level: "error",
            message,
            errorStack: error?.stack ?? "",
            payload,
          },
          true,
        ); // true = isError
      }
    } catch {
      // Logging should never break the app - fallback to console only
      console.error(`[${pkg}]`, message, error, payload);
    }
  }

  setContext(key: string, value: unknown) {
    try {
      // Sanitize value before storing to prevent issues when sending to worker
      this.context[key] = sanitizeForWorker(value);
      // Context is attached to logs when batching, no need to update worker
    } catch (error) {
      // Logging should never break the app
      console.error("[Logger] Failed to set context:", error);
    }
  }

  // Cleanup method (optional, for testing or explicit cleanup)
  destroy(): void {
    if (this.batchTimer !== null) {
      window.clearInterval(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.unloadHandler) {
      window.removeEventListener("beforeunload", this.unloadHandler);
      this.unloadHandler = null;
    }

    // Flush any remaining logs
    try {
      this.flushBatch();
      if (this.worker) {
        this.worker.postMessage({ type: "FLUSH" } as WorkerMessage);
        this.worker.terminate();
      }
    } catch (error) {
      // Log error but don't throw - logging should never break the app
      console.error("[Logger] Error during destroy:", error);
      // Still try to terminate worker
      if (this.worker) {
        try {
          this.worker.terminate();
        } catch {
          // Ignore termination errors
        }
      }
    }
  }
}

// Create logger instance immediately (before initialization) and expose globally
const loggerInstance = new LoggerImpl();
globalLoggerInstance = loggerInstance;
window.logger = loggerInstance;

/**
 * Log function that works before logger initialization
 * Buffers logs and processes them once logger is ready
 * Outputs to console and sends to worker (if enabled)
 * Console output is handled by logger methods to avoid duplication
 */
export function log(
  level: "debug" | "info" | "warn" | "error",
  pkg: string,
  message: string,
  error?: Error,
  payload?: unknown,
): void {
  // Use logger instance (works before and after initialization)
  // Logger methods handle console output, so we don't duplicate it here
  if (globalLoggerInstance) {
    switch (level) {
      case "debug":
        globalLoggerInstance.debug(pkg, message, payload);
        break;
      case "info":
        globalLoggerInstance.info(pkg, message, payload);
        break;
      case "warn":
        globalLoggerInstance.warn(pkg, message, payload);
        break;
      case "error":
        globalLoggerInstance.error(pkg, message, error, payload);
        break;
    }
  } else {
    // Fallback: if logger instance is somehow not available, output to console
    // This should not happen in normal operation
    const prefix = `[${pkg}]`;
    const consoleArgs: unknown[] = [prefix, message];
    if (payload !== undefined) consoleArgs.push(payload);
    if (error) consoleArgs.push(error);

    switch (level) {
      case "debug":
        console.debug(...consoleArgs);
        break;
      case "info":
        console.log(...consoleArgs);
        break;
      case "warn":
        console.warn(...consoleArgs);
        break;
      case "error":
        console.error(...consoleArgs);
        break;
    }
  }
}

/**
 * Convenience functions for easier logging
 * These work before logger initialization and buffer logs
 */
export function logDebug(
  pkg: string,
  message: string,
  payload?: unknown,
): void {
  log("debug", pkg, message, undefined, payload);
}

export function logInfo(pkg: string, message: string, payload?: unknown): void {
  log("info", pkg, message, undefined, payload);
}

export function logWarn(pkg: string, message: string, payload?: unknown): void {
  log("warn", pkg, message, undefined, payload);
}

export function logError(
  pkg: string,
  message: string,
  error?: Error,
  payload?: unknown,
): void {
  log("error", pkg, message, error, payload);
}
